import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../../context/AuthContext";
import { Button } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import "./Batch.css";
import PageTitle from "../../../../jsx/layouts/PageTitle";
import { getAllSportList, getAllDistrictList } from "../../../../services/commonApiService/CommonApiService";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Select from "react-select";
import { createBatch, UpdateBatch, getBatchById, getBatchAthletes, getAllSupervisorList, getAdminMe, getCoachMe } from "../../../../services/adminApiService/batchManagementApi/BatchManagementApiService";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { SPECIAL_ACADEMY_CLUSTERS } from "../specialClusters";

/* ================= VALIDATION SCHEMA ================= */
const BATCH_NAME_PATTERN = /^[\p{L}\p{N}\s\-_.]+$/u;

const schema = yup.object().shape({
  batchName: yup
    .string()
    .trim()
    .max(255, "Batch name must be at most 255 characters")
    .matches(BATCH_NAME_PATTERN, "Batch name can only contain letters, numbers, spaces, hyphens, underscores and periods")
    .required("Batch name is required"),

  sport: yup
    .string()
    .required("Sport is required"),
});
const CreateBatch = () => {
  const { id } = useParams(); // if id exists = edit mode
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const isCoach = localStorage.getItem("category_type") === "2";

  const isEditMode = !!id;
  /* ================= REACT HOOK FORM ================= */
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [search, setSearch] = useState("");
  // const [userTypeFilter, setUserTypeFilter] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [sportsList, setSportsList] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(false);
  const [supervisorList, setSupervisorList] = useState([]);
  const [supervisorLoading, setSupervisorLoading] = useState(false);
  const [districtList, setDistrictList] = useState([]);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [editBatchRaw, setEditBatchRaw] = useState(null);
  // const [userTypeList, setUserTypeList] = useState([]);
  // const [userTypeLoading, setUserTypeLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [athletes, setAthletes] = useState([]);
  const [totalAthletes, setTotalAthletes] = useState(0);
  const [loadingAthletes, setLoadingAthletes] = useState(false);
  const [coachProfile, setCoachProfile] = useState(null);

  // ================= FETCH COACH PROFILE (when SSO login has no userData) =================
  useEffect(() => {
    if (!isCoach) return;
    const userType = localStorage.getItem("user_type");
    if (userType === "user") {
      // SSO coach: use me-coach endpoint (auth:admin,api) to get profile with district_id
      getCoachMe()
        .then((res) => setCoachProfile(res?.data?.data || null))
        .catch(() => {});
      return;
    }
    getAdminMe()
      .then((res) => setCoachProfile(res?.data?.data || null))
      .catch(() => {});
  }, [isCoach]);

  // ================= SPORTS API =================
  useEffect(() => {
    const fetchSportsList = async () => {
      try {
        setSportsLoading(true);
        const response = await getAllSportList();
        setSportsList(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching sports list:", error);
      } finally {
        setSportsLoading(false);
      }
    };

    const fetchSupervisors = async () => {
      try {
        setSupervisorLoading(true);
        const response = await getAllSupervisorList();
        setSupervisorList(
          (response?.data?.data || []).map((s) => ({
            value: s.id,
            label: `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.email,
          }))
        );
      } catch (error) {
        console.error("Error fetching supervisors:", error);
      } finally {
        setSupervisorLoading(false);
      }
    };

    const fetchDistricts = async () => {
      try {
        setDistrictLoading(true);
        const response = await getAllDistrictList(4016); // 4016 = Uttarakhand state ID
        setDistrictList([
          ...(response?.data || []).map((d) => ({ value: d.id, label: d.name })),
          ...SPECIAL_ACADEMY_CLUSTERS.map((c) => ({ value: c.id, label: c.name })),
        ]);
      } catch (error) {
        console.error("Error fetching districts:", error);
      } finally {
        setDistrictLoading(false);
      }
    };

    fetchSportsList();
    fetchSupervisors();
    fetchDistricts();
  }, []);

  // useEffect(() => {
  //   const fetchUserTypes = async () => {
  //     try {
  //       setUserTypeLoading(true);
  //       const response = await UserMenu();
  //       setUserTypeList(response?.data?.data || []);
  //     } catch (error) {
  //       console.error("Error fetching user types:", error);
  //     } finally {
  //       setUserTypeLoading(false);
  //     }
  //   };

  //   fetchUserTypes();
  // }, []);

  /* ================= LOAD EDIT DATA ================= */
  useEffect(() => {
    const fetchBatchDetails = async () => {
      if (!isEditMode) return;

      try {
        const res = await getBatchById(id);
        const batch = res?.data?.data

        if (batch) {
          setValue("batchName", batch?.batch_name);
          setValue("sport", batch?.sport_id);
          setEditBatchRaw(batch);
          setSelectedAthletes(
            (batch?.athletes || []).map((athlete) => ({
              id: athlete?.athlete_id,
              name: `${athlete?.first_name || ""} ${athlete?.middle_name || ""} ${athlete?.last_name || ""}`.trim(),
              atom_id: athlete?.atom_id || "",
            }))
          );
        }
      } catch (err) {
        console.error("Error loading batch details:", err);
      }
    };

    fetchBatchDetails();
  }, [id, isEditMode, setValue]);


  /* ================= PREFILL REACT-SELECT AFTER LISTS LOAD ================= */
  useEffect(() => {
    if (!editBatchRaw || supervisorLoading || districtLoading) return;

    if (editBatchRaw.cluster) {
      const districtOpt = districtList.find((d) => d.label === editBatchRaw.cluster)
        || { value: editBatchRaw.cluster, label: editBatchRaw.cluster };
      setValue("cluster", districtOpt);
    }

    if (editBatchRaw.supervisor_id) {
      const supervisorOpt = supervisorList.find((s) => s.value === editBatchRaw.supervisor_id)
        || { value: editBatchRaw.supervisor_id, label: editBatchRaw.supervisor_name || String(editBatchRaw.supervisor_id) };
      setValue("supervisor_id", supervisorOpt);
    }
  }, [editBatchRaw, supervisorLoading, districtLoading, districtList, supervisorList, setValue]);

  /* ================= COACH AUTO-FILL ================= */
  useEffect(() => {
    if (!isCoach || supervisorLoading || districtLoading) return;
    if (isEditMode && !editBatchRaw) return;

    // Use coachProfile (from /api/admin/me) if available, fall back to userData from localStorage
    const profile = coachProfile || userData;
    if (!profile) return;

    const skipSupervisor = isEditMode && !!editBatchRaw?.supervisor_id;

    if (!skipSupervisor && profile.id) {
      const coachOpt =
        supervisorList.find((s) => String(s.value) === String(profile.id)) ||
        { value: profile.id, label: profile.name || `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || profile.email };
      setValue("supervisor_id", coachOpt);
    }
  }, [isCoach, isEditMode, supervisorLoading, districtLoading, supervisorList, userData, coachProfile, editBatchRaw, setValue]);

  /* ================= ATHLETES API ================= */

  useEffect(() => {
    const fetchAthletes = async () => {
      try {
        setLoadingAthletes(true);

        const payload = {
          per_page: perPage,
          // sub_category_type: userTypeFilter,
          sport_id: sportFilter,
          search,
          page: currentPage
        };
        const res = await getBatchAthletes(payload);
        setAthletes(res?.data?.data || []);
        setTotalAthletes(res?.data?.pagination?.total || 0);
      } catch (error) {
        console.error("Athletes fetch error:", error);
      } finally {
        setLoadingAthletes(false);
      }
    };

    fetchAthletes();
  }, [search, sportFilter, currentPage, perPage]);

  // ======= Checkbox Handler =======

  const handleSelectAthlete = (athlete) => {
    const exist = selectedAthletes.find(
      (a) => a.id === athlete.id || a.id === athlete.athlete_id
    );

    if (exist) {
      setSelectedAthletes(selectedAthletes.filter((a) => a.id !== exist.id));
    } else {
      setSelectedAthletes([
        ...selectedAthletes,
        {
          id: athlete?.id,
          name: `${athlete?.first_name || ""} ${athlete?.middle_name || ""} ${athlete?.last_name || ""}`,
          atom_id: athlete?.atom_id
        }
      ]);
    }
  };

  const removeAthlete = (id) => {
    setSelectedAthletes(selectedAthletes.filter((a) => a.id !== id));
  };

  /* ================= SUBMIT ================= */
  const onSubmit = async (data) => {
    try {
      setSaving(true);
      const payload = {
        batch_name:    data?.batchName,
        sport_id:      data?.sport,
        cluster:       data?.cluster?.label || null,
        supervisor_id: data?.supervisor_id?.value || null,
        athlete_ids:   selectedAthletes?.map((a) => a?.id),
      };

      let response;

      if (isEditMode) {
        response = await UpdateBatch(id, payload);
      } else {
        response = await createBatch(payload);
      }

      // SUCCESS CASE
      if (response?.data?.success === true) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text:
            response?.data?.message ||
            (isEditMode
              ? "Batch updated successfully"
              : "Batch created successfully"),
          timer: 2000,
          showConfirmButton: true,
        });

        navigate("/batch-management/batches");
      } else {
        // BACKEND ERROR MESSAGE
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.data?.message || "Something went wrong",
        });
      }
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong!",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (event) => {
    setCurrentPage(event?.selected + 1);
  };

  return (
    <>
      <PageTitle
        activeMenu={isEditMode ? "Edit Batch" : "Create Batch"}
        motherMenu="Batch Management"
      />
      <div >

        <h4 className="mb-4 batch-title">{isEditMode ? "Edit Batch" : "Create Batch"}</h4>

        {/* ===== Batch Form ===== */}
        <div className="card p-4 mb-4">

          {/* Back Button */}
          <div className="mb-3">
            <button
              type="button"
              className="btn back-btn"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              {/* Batch Name */}
              <div className="col-12 col-md-6 mb-3">
                <label className="form-label">
                  Batch Name <span className="text-danger">*</span>
                </label>

                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Enter Batch Name"
                  {...register("batchName")}
                />
                <span className="error">{errors.batchName?.message}</span>
              </div>
              <div className="col-12 col-md-6"></div>

              {/* Sport */}
              <div className="col-12 col-md-6 mb-3">
                <label className="form-label">
                  Sport <span className="text-danger">*</span>
                </label>

                <select
                  className="p-3 form-select mb-2"
                  {...register("sport")}
                  disabled={sportsLoading}
                >
                  <option value="">Select Sport</option>
                  {sportsList?.map((item) => (
                    <option key={item?.id} value={item?.sport_id}>
                      {item?.sport_name}
                    </option>
                  ))}
                </select>
                <span className="error">{errors.sport?.message}</span>
              </div>

              {/* Cluster (District) */}
              <div className="col-12 col-md-6 mb-3">
                <label className="form-label">Cluster</label>
                <Controller
                  name="cluster"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={districtList}
                      isLoading={districtLoading}
                      isClearable
                      isSearchable
                      placeholder="Select Cluster (District)"
                    />
                  )}
                />
              </div>

              {/* Coach / Supervisor */}
              <div className="col-12 col-md-6 mb-3">
                <label className="form-label">Coach Name</label>
                <Controller
                  name="supervisor_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={supervisorList}
                      isLoading={supervisorLoading}
                      isClearable
                      isSearchable
                      isDisabled={isCoach}
                      placeholder="Search Coach..."
                    />
                  )}
                />
              </div>

            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="light back-btn" onClick={() => navigate(-1)}>
                Cancel
              </Button>

              <Button variant="primary" type="submit">
                Save
              </Button>
            </div>
          </form>
        </div>

        {/* ===== Selected Athletes (Only show if selected) ===== */}
        {selectedAthletes.length > 0 && (
          <div className="card p-4 mb-4">

            <h6>Selected Athletes ({selectedAthletes.length} Athletes selected)</h6>

            <div className="d-flex flex-wrap gap-2 mt-3">
              {selectedAthletes?.map((athlete) => (
                <div key={athlete?.id} className="selected-chip">
                  {athlete?.name}
                  <br />
                  {athlete?.atom_id}
                  <span onClick={() => removeAthlete(athlete?.id)} className="cross-design">×</span>
                </div>
              ))}
            </div>

          </div>
        )}

        {/* ===== Add Athletes Table ===== */}
        <div className="card p-4">

          <h6 className="batch-title">Add Athletes</h6>

          {/* ===== Filters ===== */}
          <div className="row mt-3 mb-3 g-2">

            {/* Search */}
            <div className="col-12 col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search atom id, user name, email..."
                value={search}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSearch(e.target.value);
                }}
              />
            </div>

            {/* User Type */}
            {/* <div className="col-12 col-md-3">
              <select
                className="form-select"
                style={{ padding: '0.9rem' }}
                value={userTypeFilter}
                onChange={(e) => setUserTypeFilter(e.target.value)}
              >
                {userTypeLoading ? (
                  <option>Loading...</option>
                ) : (
                  <>
                    <option value="">User Type</option>
                    {userTypeList?.map((item) => (
                      <option key={item?.id} value={item?.id}>
                        {item?.category_name}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div> */}


            {/* Sport */}
            <div className="col-12 col-md-3">
              <select
                className="form-select"
                style={{ padding: '0.9rem' }}
                value={sportFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSportFilter(e.target.value);
                }}
              >
                <option value="">Select Sport</option>
                {sportsList?.map((item) => (
                  <option key={item?.id} value={item?.sport_id}>
                    {item?.sport_name}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* ===== Table ===== */}
          <div className="table-responsive">
            <table className="table">
              <thead className="bg-primary text-white">
                <tr>
                  <th></th>
                  <th>Atom ID</th>
                  <th>User Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  {/* <th>User Type</th> */}
                  <th>Sport</th>
                </tr>
              </thead>

              <tbody>
                {athletes?.length > 0 ? (
                  athletes?.map((athlete) => (
                    <tr key={athlete?.id}>

                      <td>
                        <input
                          type="checkbox"
                          checked={selectedAthletes.some(
                            (a) => a.id === athlete?.id
                          )}
                          onChange={() => handleSelectAthlete(athlete)}
                        />
                      </td>

                      <td>{athlete?.atom_id}</td>
                      <td>{`${athlete?.first_name || ""} ${athlete?.middle_name || ""} ${athlete?.last_name || ""}`}</td>
                      <td>{athlete?.mobile || "—"}</td>
                      <td>{athlete?.email}</td>
                      {/* <td>{athlete?.user_type_name}</td> */}
                      <td>{athlete?.sport_name}</td>
                    </tr>

                  ))

                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No athletes found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ===== Pagination Section ===== */}
          <div className="d-flex justify-content-between align-items-center mt-3">


            <ReactPaginate
              nextLabel="next >"
              onPageChange={handlePageChange}
              pageRangeDisplayed={5}
              marginPagesDisplayed={2}
              pageCount={Math.ceil(totalAthletes / perPage)}
              previousLabel="< previous"
              containerClassName="pagination d-flex flex-wrap"
              activeClassName="active"
              pageClassName="page-item mx-1 my-1"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link mx-1 my-1"
              nextClassName="page-item mx-1 my-1"
              nextLinkClassName="page-link"
              breakLabel="..."
              breakClassName="page-item mx-1 my-1"
              breakLinkClassName="page-link"
              forcePage={currentPage - 1}
            />
          </div>
        </div>

      </div>
    </>
  );
};

export default CreateBatch;